import * as React from "react";
import { toast } from "react-toastify";
import {
  useCreateSnapshotReviewRunMutation,
  useLazyGetSnapshotReviewRunQuery,
  type SnapshotGridField,
} from "@/modules/snapshots/businessReports.api";
import type { BusinessReport } from "@/modules/snapshots/businessReports.types";
import type { ColumnReviewInfo } from "@/modules/snapshots/grid/sortAndFilter";

export const REVIEW_RUN_POLL_INTERVAL_MS = 4000;

interface Input {
  collectionId: string | undefined;
  fields: SnapshotGridField[];
  businessReportPrompts: BusinessReport["prompts"] | undefined;
  refetch: () => unknown;
}

/** Column review triage: per-column review eligibility, starting review runs and polling them to completion. */
export const useColumnReviewRuns = ({ collectionId, fields, businessReportPrompts, refetch }: Input) => {
  const [reviewDialogColumn, setReviewDialogColumn] = React.useState<{ header: string; promptId: string } | null>(null);
  const [reviewingPromptIds, setReviewingPromptIds] = React.useState<string[]>([]);
  const [createReviewRun, { isLoading: isStartingReview }] = useCreateSnapshotReviewRunMutation();
  const [fetchReviewRun] = useLazyGetSnapshotReviewRunQuery();

  const columnReview = React.useMemo<Record<string, ColumnReviewInfo>>(() => {
    const promptsById = new Map((businessReportPrompts ?? []).map(prompt => [prompt.id, prompt]));
    const out: Record<string, ColumnReviewInfo> = {};
    for (const field of fields) {
      if (field.is_note || !field.business_report_prompt_id) continue;
      const prompt = promptsById.get(field.business_report_prompt_id);
      const eligible = !prompt || (!prompt.use_image && !prompt.web_search);
      out[field.name] = { promptId: field.business_report_prompt_id, eligible, summary: field.review ?? null };
    }
    return out;
  }, [fields, businessReportPrompts]);

  React.useEffect(() => {
    const inProgress = fields
      .filter(field => field.review?.status === "IN_PROGRESS" && field.business_report_prompt_id)
      .map(field => field.business_report_prompt_id as string);
    if (inProgress.length === 0) return;
    setReviewingPromptIds(previous => {
      const merged = [...previous];
      for (const promptId of inProgress) {
        if (!merged.includes(promptId)) merged.push(promptId);
      }
      return merged.length === previous.length ? previous : merged;
    });
  }, [fields]);

  // Progress for every reviewing column is fetched in parallel each tick; the grid (the heaviest call on
  // the page) is refetched only when some column's progress payload changed since the previous tick, and
  // a hidden tab skips the tick entirely. The WebSocket completion event still refreshes the grid.
  const lastProgressSnapshot = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (reviewingPromptIds.length === 0 || !collectionId) return;
    let cancelled = false;
    lastProgressSnapshot.current = null;
    const interval = window.setInterval(async () => {
      if (document.hidden) return;
      const settled = await Promise.all(
        reviewingPromptIds.map(promptId =>
          fetchReviewRun({ snapshotCollectionId: collectionId, businessReportPromptId: promptId })
            .unwrap()
            .then(progress => ({ promptId, progress }))
            .catch(() => ({ promptId, progress: null })),
        ),
      );
      if (cancelled) return;
      const finished: string[] = [];
      for (const { promptId, progress } of settled) {
        const status = progress?.run?.status;
        if (status === "IN_PROGRESS") continue;
        finished.push(promptId);
        if (status === "COMPLETED") {
          toast.success("Review completed");
        } else if (status === "FAILED") {
          toast.error("Review failed");
        }
      }
      const progressSnapshot = JSON.stringify(settled);
      if (progressSnapshot !== lastProgressSnapshot.current) {
        lastProgressSnapshot.current = progressSnapshot;
        refetch();
      }
      if (finished.length > 0) {
        setReviewingPromptIds(previous => previous.filter(promptId => !finished.includes(promptId)));
      }
    }, REVIEW_RUN_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [reviewingPromptIds, collectionId, fetchReviewRun, refetch]);

  const handleReviewColumnClick = React.useCallback(
    (header: string) => {
      const info = columnReview[header];
      if (info) setReviewDialogColumn({ header, promptId: info.promptId });
    },
    [columnReview],
  );

  const handleConfirmReviewRun = async (executionMode: "sync" | "batch") => {
    if (!collectionId || !reviewDialogColumn) return;
    try {
      await createReviewRun({
        snapshotCollectionId: collectionId,
        businessReportPromptId: reviewDialogColumn.promptId,
        executionMode,
      }).unwrap();
      const promptId = reviewDialogColumn.promptId;
      setReviewingPromptIds(previous => (previous.includes(promptId) ? previous : [...previous, promptId]));
      toast.success(`Reviewing responses for "${reviewDialogColumn.header}"`);
      setReviewDialogColumn(null);
    } catch (error) {
      const apiError = error as { status?: number; data?: { detail?: string } };
      if (apiError?.status === 409) {
        toast.error("A review is already running for this column.");
      } else {
        toast.error(apiError?.data?.detail ?? "Failed to start review");
      }
    }
  };

  const closeReviewDialog = () => setReviewDialogColumn(null);

  return {
    columnReview,
    reviewDialogColumn,
    closeReviewDialog,
    handleReviewColumnClick,
    handleConfirmReviewRun,
    isStartingReview,
  };
};
